import { useEffect, useRef } from 'react';

/**
 * Plays call tones using the Web Audio API.
 *
 * - Dialling: a steady dual-tone (350Hz + 440Hz) pulsing on/off, similar to a phone dial tone.
 * - Ringing: a cheerful ascending three-note melody that repeats.
 */
export function useCallTones({
  isDialling,
  isRinging,
}: {
  isDialling: boolean;
  isRinging: boolean;
}) {
  useDiallingTone(isDialling);
  useRingingTone(isRinging);
}

/**
 * DTMF frequency pairs for phone keypad digits.
 * Each key is a combination of one row frequency and one column frequency.
 */
const DTMF_TONES: [number, number][] = [
  [941, 1336], // 0
  [697, 1209], // 1
  [697, 1336], // 2
  [697, 1477], // 3
  [770, 1209], // 4
  [770, 1336], // 5
  [770, 1477], // 6
  [852, 1209], // 7
  [852, 1336], // 8
  [852, 1477], // 9
];

/**
 * Dialling tone: plays a rapid sequence of DTMF keypad tones,
 * like someone punching in a phone number.
 * Plays once when placing the call (no repeat).
 */
function useDiallingTone(isActive: boolean) {
  const ctxRef = useRef<AudioContext | null>(null);

  useEffect(() => {
    if (!isActive) return;

    const ctx = new AudioContext();
    ctxRef.current = ctx;

    // Play 11-14 DTMF tones with realistic timing (~3.5-4.5s of dialling)
    const digitCount = 11 + Math.floor(Math.random() * 4);
    let offset = 0;
    for (let i = 0; i < digitCount; i++) {
      const [freqLow, freqHigh] =
        DTMF_TONES[Math.floor(Math.random() * DTMF_TONES.length)];
      playTone(ctx, freqLow, 'sine', offset, 0.18, 0.06);
      playTone(ctx, freqHigh, 'sine', offset, 0.18, 0.06);
      // Vary the gap between digits for a natural feel
      offset += 0.18 + 0.08 + Math.random() * 0.1;
    }

    return () => {
      ctx.close();
      ctxRef.current = null;
    };
  }, [isActive]);
}

/**
 * Ringing tone: cheerful ascending three-note melody (C5, E5, G5)
 * played twice per cycle with a pause between.
 */
function useRingingTone(isActive: boolean) {
  const ctxRef = useRef<AudioContext | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!isActive) return;

    const ctx = new AudioContext();
    ctxRef.current = ctx;

    const playMelody = () => {
      // First phrase: C5 → E5 → G5
      playTone(ctx, 523, 'sine', 0, 0.15, 0.12); // C5
      playTone(ctx, 659, 'sine', 0.18, 0.15, 0.12); // E5
      playTone(ctx, 784, 'sine', 0.36, 0.22, 0.12); // G5

      // Second phrase (repeat after short gap)
      playTone(ctx, 523, 'sine', 0.8, 0.15, 0.12);
      playTone(ctx, 659, 'sine', 0.98, 0.15, 0.12);
      playTone(ctx, 784, 'sine', 1.16, 0.22, 0.12);
    };

    playMelody();
    intervalRef.current = setInterval(playMelody, 3000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      ctx.close();
      ctxRef.current = null;
    };
  }, [isActive]);
}

function playTone(
  ctx: AudioContext,
  frequency: number,
  type: OscillatorType,
  delaySeconds: number,
  durationSeconds: number,
  volume: number,
) {
  const oscillator = ctx.createOscillator();
  const gain = ctx.createGain();

  oscillator.type = type;
  oscillator.frequency.value = frequency;

  const start = ctx.currentTime + delaySeconds;
  const end = start + durationSeconds;

  // Fade in/out to avoid clicks
  gain.gain.setValueAtTime(0, start);
  gain.gain.linearRampToValueAtTime(volume, start + 0.02);
  gain.gain.setValueAtTime(volume, end - 0.02);
  gain.gain.linearRampToValueAtTime(0, end);

  oscillator.connect(gain);
  gain.connect(ctx.destination);

  oscillator.start(start);
  oscillator.stop(end + 0.01);
}
